import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Home, ExternalLink } from "lucide-react";
import { useGTMTracking } from "@/hooks/useGTMTracking";
import { logFormError } from "@/lib/logFormError";
import ZoomScheduler from "./ZoomScheduler";
import { SectionBadge } from "@/components/ui/section-badge";
import { useTranslation } from "react-i18next";
import { trackGA4Event } from "@/lib/ga4Tracking";
import { trackFormConversion, trackThankYouView } from "@/lib/formConversions";

interface ConsultationFormProps {
  onSuccess?: () => void;
  title?: string;
  showSchedulingOptions?: boolean;
}

type FormStep = 'form' | 'schedule' | 'done';

const ConsultationForm = ({ onSuccess, title, showSchedulingOptions = false }: ConsultationFormProps) => {
  const { t } = useTranslation();
  const displayTitle = title || t('consultationForm.defaultTitle');
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    interest: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<FormStep>('form');
  const [formStartTime, setFormStartTime] = useState<number | null>(null);
  const [meetingData, setMeetingData] = useState<any>(null);
  
  const { toast } = useToast();
  const { trackFormStart, trackFormCompletion, trackFormSubmission, trackButtonClick, trackEvent } = useGTMTracking();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (!formStartTime && name === 'name' && value.length === 1) {
      const startTime = Date.now();
      setFormStartTime(startTime);
      trackFormStart('consultation_form');
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, interest: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast({ title: t('consultationForm.missingInfo'), description: t('consultationForm.missingInfoDesc'), variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-consultation-email', { body: formData });
      if (error) throw error;
      if (formStartTime) {
        const completionTime = (Date.now() - formStartTime) / 1000;
        trackFormCompletion('consultation_form', completionTime);
      }
      trackFormSubmission('consultation_form', true);
      trackGA4Event('sign_up', {
        params: { method: 'consultation_form', plan: 'lead', is_new_user: true, interest: formData.interest || undefined, source: 'consultation_form' },
      });
      trackFormConversion({ form_name: 'consultation_form', form_category: 'consultation', interest: formData.interest });
      trackThankYouView({ form_name: 'consultation_form', form_category: 'consultation' });
      toast({ title: t('consultationForm.thankYou'), description: t('consultationForm.thankYouDesc') });
      setCurrentStep('done');
    } catch (error) {
      console.error("Error sending consultation request:", error);
      logFormError('consultation_form', error, formData);
      trackFormSubmission('consultation_form', false);
      toast({ title: t('consultationForm.errorSending'), description: t('consultationForm.errorSendingDesc'), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMeetingCreated = (createdMeetingData: any) => {
    setMeetingData(createdMeetingData);
    setCurrentStep('done');
    trackEvent('meeting_scheduled', { source: 'consultation_form', provider: 'zoom', meeting_id: createdMeetingData.id });
  };

  const handleBackToForm = () => setCurrentStep('form');
  const handleClose = () => onSuccess?.();
  const isFormValid = !!(formData.name && formData.email && formData.message);

  if (currentStep === 'schedule' && showSchedulingOptions) {
    return (
      <div className="space-y-6">
        <ZoomScheduler formData={formData} onMeetingCreated={handleMeetingCreated} onBack={handleBackToForm} />
      </div>
    );
  }

  if (currentStep === 'done') {
    return (
      <div className="text-center space-y-6">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">✓</span>
          </div>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-foreground mb-2">
            {meetingData ? t('consultationForm.meetingScheduled') : t('consultationForm.requestSubmitted')}
          </h3>
          <p className="text-lg text-muted-foreground">
            {meetingData ? t('consultationForm.meetingConfirmed') : t('consultationForm.requestReceived')}
          </p>
        </div>
        {meetingData && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
            <div className="text-sm font-medium text-primary">
              <p><strong>{t('consultationForm.date')}</strong> {meetingData.formattedDate}</p>
              <p><strong>{t('consultationForm.time')}</strong> {meetingData.formattedTime}</p>
              <p><strong>{t('consultationForm.meetingId')}</strong> {meetingData.id}</p>
            </div>
            <Button asChild size="sm" className="w-full">
              <a href={meetingData.join_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('consultationForm.joinMeeting')}
              </a>
            </Button>
          </div>
        )}
        <div className="space-y-3">
          <p className="text-muted-foreground">
            {meetingData ? t('consultationForm.expertPrepared') : t('consultationForm.teamReview')}
          </p>
          <div className="flex flex-col gap-3 sm:grid sm:w-fit sm:grid-flow-col sm:auto-cols-fr sm:mx-auto">
            {!meetingData && (
              <Button onClick={() => { trackEvent('schedule_demo_click', { source: 'consultation_form' }); window.location.href = '/book-demo'; }} className="w-full flex items-center justify-center gap-2" variant="default">
                <ExternalLink className="w-4 h-4" />
                {t('consultationForm.bookTimeNow')}
              </Button>
            )}
            <Button onClick={handleClose} className="flex items-center gap-2 w-full" variant={meetingData ? "default" : "outline"}>
              <Home className="w-4 h-4" />
              {t('consultationForm.returnHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-center mb-2">{displayTitle}</h2>
        <p className="text-muted-foreground text-center">{t('consultationForm.subtitle')}</p>
      </div>
      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">{t('consultationForm.fullName')}</Label>
          <Input id="name" name="name" type="text" placeholder={t('consultationForm.fullNamePlaceholder')} value={formData.name} onChange={handleInputChange} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t('consultationForm.emailAddress')}</Label>
          <Input id="email" name="email" type="email" placeholder={t('consultationForm.emailPlaceholder')} value={formData.email} onChange={handleInputChange} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">{t('consultationForm.company')}</Label>
          <Input id="company" name="company" type="text" placeholder={t('consultationForm.companyPlaceholder')} value={formData.company} onChange={handleInputChange} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="interest">{t('consultationForm.areaOfInterest')}</Label>
          <Select value={formData.interest} onValueChange={handleSelectChange}>
            <SelectTrigger><SelectValue placeholder={t('consultationForm.selectInterest')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="compliance">{t('consultationForm.compliance')}</SelectItem>
              <SelectItem value="cybersecurity">{t('consultationForm.cybersecurity')}</SelectItem>
              <SelectItem value="analytics">{t('consultationForm.analytics')}</SelectItem>
              <SelectItem value="audit">{t('consultationForm.audit')}</SelectItem>
              <SelectItem value="enterprise">{t('consultationForm.enterpriseSolution')}</SelectItem>
              <SelectItem value="demo">{t('consultationForm.schedulePlatformDemo')}</SelectItem>
              <SelectItem value="discovery">{t('consultationForm.discoveryCall')}</SelectItem>
              <SelectItem value="learn-more">{t('consultationForm.learnMore')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="message">{t('consultationForm.message')}</Label>
          <Textarea id="message" name="message" placeholder={t('consultationForm.messagePlaceholder')} value={formData.message} onChange={handleInputChange} rows={4} required />
        </div>
        <Button type="submit" className="w-full" disabled={!isFormValid || isLoading}>
          {isLoading ? t('consultationForm.sending') : t('consultationForm.sendRequest')}
        </Button>
      </form>
    </div>
  );
};

export default ConsultationForm;
